import React from "react";
import { MessageCircle, Mail, Twitter, Linkedin, Github, ArrowUpRight } from "lucide-react";

const navLinks = [
  { href: "#services", label: "Services" },
  { href: "#process", label: "Process" },
  { href: "#automations", label: "Automations" },
  { href: "#about", label: "About" },
  { href: "#cta", label: "Contact" },
];

const services = [
  "WhatsApp Automation",
  "AI Chatbots",
  "Workflow Automation",
  "CRM Automation",
  "AI Voice Agents",
  "Lead Generation",
];

const socials = [
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Github, href: "#", label: "GitHub" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative border-t border-white/[0.05] bg-[#07070A]">
      {/* Subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-14">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/brand/aiflow-icon.svg" alt="AIFlow Labs" className="w-8 h-8 rounded-xl" />
              <span className="font-semibold text-[16px] tracking-tight text-white/90">
                AIFlow <span className="text-white/30 font-normal">Labs</span>
              </span>
            </div>
            <p className="text-[13px] text-white/32 leading-[1.7] mb-6 max-w-[220px]">
              AI automation systems built for modern businesses. We automate the manual.
            </p>

            {/* Contact */}
            <div className="space-y-2.5">
              <a
                href="https://wa.me/918248195127"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-2 text-[13px] text-white/32 hover:text-emerald-400 transition-colors duration-200"
              >
                <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                +91 82481 95127
              </a>
              <a
                href="mailto:mohinish24@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-2 text-[13px] text-white/32 hover:text-blue-400 transition-colors duration-200"
              >
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                mohinish24@gmail.com
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] text-white/20 uppercase mb-5">Navigation</p>
            <ul className="space-y-2.5">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-[13px] text-white/38 hover:text-white/80 transition-colors duration-200">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] text-white/20 uppercase mb-5">Services</p>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s}>
                  <a href="#services" className="text-[13px] text-white/38 hover:text-white/80 transition-colors duration-200">
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA card */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] text-white/20 uppercase mb-5">Get Started</p>
            <div className="p-5 rounded-2xl border border-white/[0.06] bg-[#0E0E12] card-inner-top">
              <p className="text-[13.5px] font-semibold text-white/80 mb-2 tracking-[-0.01em]">Free Strategy Call</p>
              <p className="text-[12px] text-white/32 mb-4 leading-[1.6]">
                30-minute call to map your automation roadmap. No commitment.
              </p>
              <a
                href="#cta"
                data-testid="footer-cta"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-400/80 hover:text-blue-300 transition-colors group"
              >
                Book now
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-white/20 font-mono">
            © {new Date().getFullYear()} AIFlow Labs. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            {socials.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="text-white/20 hover:text-white/55 transition-colors duration-200"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
